import React, { useState } from "react";
import { ShieldCheck, Info, X, BookOpen, BarChart3 } from "lucide-react";

/**
 * ConfidenceBadge — Clinical Confidence Score widget for AI-generated content.
 *
 * Props:
 *   confidence:  "high" | "moderate" | "low"
 *   evidenceClass:  "I" | "IIa" | "IIb" | "III"  (ACC/AHA convention) — optional
 *   sources:     [{ name, year, url? }, ...]  — optional, shown in popover
 *   modelRef:    "Claude Sonnet 4.5" | "Web Speech API" | ...  — optional
 *   compact:     boolean — small inline pill vs full block
 *   testid:      string
 */
const COLORS = {
  high:     { bg: "#ECFDF5", text: "#065F46", border: "#10B981", dot: "#10B981" },
  moderate: { bg: "#FEF3C7", text: "#92400E", border: "#F59E0B", dot: "#F59E0B" },
  low:      { bg: "#FEE2E2", text: "#991B1B", border: "#EF4444", dot: "#EF4444" },
};

const EVIDENCE_CLASS_DESCRIPTIONS = {
  "I":   "Strong recommendation — benefit clearly outweighs risk (ACC/AHA Class I).",
  "IIa": "Moderate recommendation — benefit > risk; reasonable to perform (Class IIa).",
  "IIb": "Weak recommendation — benefit ≥ risk; may be considered (Class IIb).",
  "III": "No benefit / harmful — recommendation against (Class III).",
};

export default function ConfidenceBadge({
  confidence = "moderate",
  evidenceClass = null,
  sources = [],
  modelRef = null,
  compact = false,
  testid = "confidence-badge",
}) {
  const [open, setOpen] = useState(false);
  const c = COLORS[confidence] || COLORS.moderate;
  const label = confidence === "high" ? "High confidence"
              : confidence === "low"  ? "Low confidence"
              : "Moderate confidence";

  if (compact) {
    return (
      <span
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold cursor-pointer hover:opacity-80 select-none"
        style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
        data-testid={testid}
        title="View clinical confidence details"
      >
        <ShieldCheck className="w-3 h-3" /> {label}
        {evidenceClass && <span className="ml-1 opacity-70">· Class {evidenceClass}</span>}
        {open && <DetailsModal {...{ confidence, evidenceClass, sources, modelRef, onClose: () => setOpen(false), testid }} />}
      </span>
    );
  }

  return (
    <div
      className="rounded-md border p-3 flex items-start gap-3"
      style={{ background: c.bg, color: c.text, borderColor: c.border }}
      data-testid={testid}
    >
      <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm">{label}</span>
          {evidenceClass && (
            <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full"
              style={{ background: "rgba(255,255,255,0.7)", color: c.text }}>
              ACC/AHA Class {evidenceClass}
            </span>
          )}
          <button
            onClick={() => setOpen(true)}
            className="ml-auto text-[10px] underline hover:no-underline opacity-80"
            data-testid={`${testid}-details`}
          >
            View sources & reasoning
          </button>
        </div>
        {evidenceClass && (
          <div className="text-[11px] mt-1 leading-relaxed opacity-90">
            {EVIDENCE_CLASS_DESCRIPTIONS[evidenceClass]}
          </div>
        )}
        {sources.length > 0 && (
          <div className="text-[10px] mt-2 opacity-80">
            Based on {sources.length} source{sources.length > 1 ? "s" : ""} · click for details
          </div>
        )}
      </div>
      {open && <DetailsModal {...{ confidence, evidenceClass, sources, modelRef, onClose: () => setOpen(false), testid }} />}
    </div>
  );
}

function DetailsModal({ confidence, evidenceClass, sources, modelRef, onClose, testid }) {
  const c = COLORS[confidence] || COLORS.moderate;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
         onClick={onClose}
         data-testid={`${testid}-modal`}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 relative"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
                data-testid={`${testid}-close`}>
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5" style={{ color: c.dot }} />
          <div className="text-base font-semibold text-slate-900">Clinical Confidence Score</div>
        </div>
        <div className="space-y-3 text-sm text-slate-700">
          <Section icon={BarChart3} title="Confidence level">
            <span className="font-semibold capitalize" style={{ color: c.text }}>{confidence}</span>
            {confidence === "high"     && " — strong agreement across multiple high-quality sources."}
            {confidence === "moderate" && " — supported by guidelines but with some clinical heterogeneity."}
            {confidence === "low"      && " — limited evidence; physician judgement is the primary basis."}
          </Section>
          {evidenceClass && (
            <Section icon={Info} title={`Evidence class ${evidenceClass}`}>
              {EVIDENCE_CLASS_DESCRIPTIONS[evidenceClass]}
            </Section>
          )}
          {sources.length > 0 && (
            <Section icon={BookOpen} title="Sources">
              <ul className="space-y-1 mt-1">
                {sources.map((s, i) => (
                  <li key={i} className="text-xs leading-snug">
                    {s.url
                      ? <a href={s.url} target="_blank" rel="noreferrer" className="underline hover:no-underline">{s.name}{s.year ? ` (${s.year})` : ""}</a>
                      : <>{s.name}{s.year ? ` (${s.year})` : ""}</>
                    }
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {modelRef && (
            <Section icon={Info} title="AI model">
              {modelRef}
            </Section>
          )}
          <div className="text-[10px] italic text-slate-500 border-t border-slate-200 pt-3 leading-relaxed">
            ZidaMD AI suggestions are decision support only. Physician retains full responsibility for the final clinical decision.
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
      <Icon className="w-3 h-3" /> {title}
    </div>
    <div>{children}</div>
  </div>
);
